import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FONT_OPTIONS = [
  { value: 'sans-serif', label: 'Sans-Serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond' },
  { value: 'Merriweather', label: 'Merriweather' },
  { value: 'Lora', label: 'Lora' },
  { value: 'Cinzel', label: 'Cinzel' },
  { value: 'Lucida Handwriting', label: 'Lucida Handwriting' },
  { value: 'Dancing Script', label: 'Dancing Script' },
  { value: 'Great Vibes', label: 'Great Vibes' },
  { value: 'Parisienne', label: 'Parisienne' },
  { value: 'Tangerine', label: 'Tangerine' },
  { value: 'cursive', label: 'Cursive' },
];

const FIELD_TYPES = [
  { value: 'full_name', label: 'Full Name', icon: '\u{1F464}' },
  { value: 'membership_number', label: 'Membership #', icon: '\u{1F3F7}\uFE0F' },
  { value: 'certificate_number', label: 'Certificate #', icon: '#\uFE0F\u20E3' },
  { value: 'issue_date', label: 'Issue Date', icon: '\u{1F4C5}' },
  { value: 'expiry_date', label: 'Expiry Date', icon: '\u23F0' },
  { value: 'title', label: 'Title', icon: '\u{1F4DD}' },
  { value: 'signature', label: 'Signature', icon: '\u270D\uFE0F' },
  { value: 'custom', label: 'Custom Text', icon: '\u270F\uFE0F' },
];

const resolveImgUrl = (tpl) => tpl.image_url || `${API_BASE}${tpl.image}`;


/* ================================================================
   SECTION 1 — TEMPLATES TAB
   ================================================================ */
function TemplatesTab({ onDesign }) {
  const { canAdd, canDelete } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    api.get('/admin/cert-templates/')
      .then(res => setTemplates(res.data.results || res.data))
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png'].includes(f.type)) {
      toast.error('Only JPEG and PNG files are allowed'); return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) { toast.error('Name and image are required'); return; }
    setUploading(true);
    const img = new Image();
    img.src = preview;
    await new Promise(resolve => { img.onload = resolve; });
    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('image', file);
    formData.append('width', img.naturalWidth);
    formData.append('height', img.naturalHeight);
    try {
      await api.post('/admin/cert-templates/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Template uploaded!');
      setName(''); setFile(null); setPreview(null); setShowUpload(false);
      if (fileRef.current) fileRef.current.value = '';
      fetchTemplates();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template and all its fields?')) return;
    try {
      await api.delete(`/admin/cert-templates/${id}/`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Certificate Templates</h2>
          <p className="text-sm text-gray-500 mt-0.5">Upload and manage certificate background images</p>
        </div>
        {canAdd(PERMISSIONS.ADD_CERTIFICATES) && (
          <button onClick={() => setShowUpload(!showUpload)}
            className="flex items-center gap-2 bg-[#0B5D1E] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#094d18] shadow-sm transition-all">
            {showUpload ? (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> Cancel</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg> New Template</>
            )}
          </button>
        )}
      </div>

      {/* Upload form */}
      {showUpload && (
        <form onSubmit={handleUpload} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow border p-6 space-y-4 animate-fadeIn">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Upload New Template</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Template Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required
                placeholder="e.g. Membership Certificate 2026"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none text-sm transition" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Background Image</label>
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png" onChange={handleFileChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm file:mr-3 file:px-4 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#0B5D1E] file:text-white file:font-medium file:text-sm file:cursor-pointer cursor-pointer transition" />
            </div>
          </div>
          {preview && (
            <div className="bg-gray-100 rounded-xl p-4 inline-block">
              <img src={preview} alt="Preview" className="max-h-40 rounded-lg shadow-sm" />
            </div>
          )}
          <div>
            <button type="submit" disabled={uploading}
              className="bg-[#0B5D1E] text-white px-8 py-2.5 rounded-xl font-semibold hover:bg-[#094d18] shadow-sm transition disabled:opacity-50 text-sm">
              {uploading ? 'Uploading...' : 'Upload Template'}
            </button>
          </div>
        </form>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E]" />
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-16 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No Templates Yet</h3>
          <p className="text-gray-400 text-sm">Upload your first certificate background to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(t => (
            <div key={t.id} className="bg-white rounded-2xl shadow-sm border overflow-hidden group hover:shadow-lg hover:border-[#0B5D1E]/30 transition-all duration-300">
              <div className="aspect-[1.414/1] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden relative">
                <img src={resolveImgUrl(t)} alt={t.name} className="w-full h-full object-contain p-2" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
              </div>
              <div className="p-4 border-t">
                <h3 className="font-bold text-gray-800 truncate text-sm">{t.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-400">{t.width} x {t.height}px</span>
                  <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">{t.fields_data?.length || 0} fields</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => onDesign(t)}
                    className="flex-1 bg-[#0B5D1E] text-white py-2 rounded-xl text-sm font-semibold hover:bg-[#094d18] transition shadow-sm">
                    Open Designer
                  </button>
                  {canDelete(PERMISSIONS.DELETE_CERTIFICATES) && (
                    <button onClick={() => handleDelete(t.id)}
                      className="px-3 py-2 rounded-xl text-sm text-red-500 border border-red-200 hover:bg-red-50 transition" title="Delete">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ================================================================
   SECTION 2 — CERTIFICATE DESIGNER (Drag, Drop, Resize)
   ================================================================ */
function DesignerTab({ template, onBack, onCertIssued }) {
  const { canEdit, canDelete } = usePermissions();

  // Fields state
  const [fields, setFields] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState('[]');
  const [selectedField, setSelectedField] = useState(null);
  const [saving, setSaving] = useState(false);

  // Issue form state
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [issueData, setIssueData] = useState({ member: '', certificate_number: '', title: '', issue_date: '', expiry_date: '' });
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [sourceType, setSourceType] = useState('mcpd');
  const [trainings, setTrainings] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [bulkIssueData, setBulkIssueData] = useState({ title: '', issue_date: '', expiry_date: '' });
  const [bulkLoading, setBulkLoading] = useState(false);

  // UI state
  const [sidebarTab, setSidebarTab] = useState('fields');

  // Refs
  const canvasRef = useRef(null);
  const dragRef = useRef({ active: false, mode: null, fieldId: null, startX: 0, startY: 0, startFieldX: 0, startFieldY: 0, startFieldW: 0, startFieldH: 0, canvasW: 0, canvasH: 0 });
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const imgUrl = resolveImgUrl(template);

  // Snapshot helper for dirty-checking
  const makeSnapshot = useCallback((fieldList) => JSON.stringify(fieldList.map(f => ({
    field_type: f.field_type, label: f.label,
    x: Math.round(f.x * 100) / 100, y: Math.round(f.y * 100) / 100,
    width: f.width, height: f.height, font_size: f.font_size,
    font_color: f.font_color, font_family: f.font_family || 'sans-serif',
    font_weight: f.font_weight, text_align: f.text_align,
    default_value: f.default_value || '',
  }))), []);

  const isDirty = useMemo(() => makeSnapshot(fields) !== savedSnapshot, [fields, savedSnapshot, makeSnapshot]);

  // Load fields from server
  useEffect(() => {
    api.get(`/admin/cert-templates/${template.id}/fields/`)
      .then(res => {
        const loaded = res.data.results || res.data;
        setFields(loaded);
        setSavedSnapshot(makeSnapshot(loaded));
      })
      .catch(() => toast.error('Failed to load fields'));
  }, [template.id, makeSnapshot]);

  // Member search debounced
  useEffect(() => {
    if (memberSearch.length < 2) { setMembers([]); return; }
    const timer = setTimeout(() => {
      api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}&page_size=10`)
        .then(res => setMembers(res.data.results || res.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  // Bulk source items
  useEffect(() => {
    if (bulkMode && showIssueForm) {
      api.get(`/admin/attendance-source/?source=${sourceType}`)
        .then(res => setTrainings(res.data))
        .catch(() => toast.error('Failed to load source items'));
    }
  }, [bulkMode, showIssueForm, sourceType]);

  useEffect(() => { setSelectedTraining(''); setAttendees([]); setSelectedMembers([]); }, [sourceType]);

  useEffect(() => {
    if (!selectedTraining) { setAttendees([]); setSelectedMembers([]); return; }
    api.get(`/admin/attendance-source/?source=${sourceType}&source_id=${selectedTraining}`)
      .then(res => { setAttendees(res.data); setSelectedMembers([]); })
      .catch(() => toast.error('Failed to load attendees'));
  }, [selectedTraining, sourceType]);

  // Signature upload
  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSignaturePreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append('signature_image', file);
    try {
      await api.patch(`/admin/cert-templates/${template.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Signature uploaded');
    } catch { toast.error('Failed to upload signature'); }
  };

  // Field CRUD
  const addField = (type) => {
    const existing = fields.filter(f => f.field_type === type).length;
    const yOff = existing * 6;
    const isSig = type === 'signature';
    const nf = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      field_type: type,
      label: FIELD_TYPES.find(f => f.value === type)?.label || type,
      x: 30, y: isSig ? 65 + yOff : 40 + yOff,
      width: isSig ? 15 : 35, height: isSig ? 8 : 5,
      font_size: 16, font_color: '#000000', font_family: 'sans-serif',
      font_weight: 'normal', text_align: 'center', default_value: '', _isNew: true,
    };
    setFields(prev => [...prev, nf]);
    setSelectedField(nf.id);
    setSidebarTab('properties');
  };

  const updateField = useCallback((id, updates) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const removeField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedField === id) setSelectedField(null);
  };

  // Drag & Drop
  const startDrag = (e, fieldId, mode = 'move') => {
    e.stopPropagation();
    if (e.type === 'touchstart') e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const { clientX, clientY } = e.touches ? e.touches[0] : e;
    const field = fieldsRef.current.find(f => f.id === fieldId);
    if (!field) return;
    dragRef.current = {
      active: true, mode, fieldId,
      startX: clientX, startY: clientY,
      startFieldX: field.x, startFieldY: field.y,
      startFieldW: field.width, startFieldH: field.height,
      canvasW: rect.width, canvasH: rect.height,
    };
    setSelectedField(fieldId);
    setSidebarTab('properties');
  };

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    if (e.cancelable) e.preventDefault();
    const { clientX, clientY } = e.touches ? e.touches[0] : e;
    const dx = ((clientX - d.startX) / d.canvasW) * 100;
    const dy = ((clientY - d.startY) / d.canvasH) * 100;
    if (d.mode === 'move') {
      const field = fieldsRef.current.find(f => f.id === d.fieldId);
      if (!field) return;
      const nx = Math.max(0, Math.min(100 - field.width, d.startFieldX + dx));
      const ny = Math.max(0, Math.min(100 - field.height, d.startFieldY + dy));
      setFields(prev => prev.map(f => f.id === d.fieldId ? { ...f, x: Math.round(nx * 100) / 100, y: Math.round(ny * 100) / 100 } : f));
    } else if (d.mode === 'resize') {
      const nw = Math.max(5, Math.min(100 - d.startFieldX, d.startFieldW + dx));
      const nh = Math.max(2, Math.min(100 - d.startFieldY, d.startFieldH + dy));
      setFields(prev => prev.map(f => f.id === d.fieldId ? { ...f, width: Math.round(nw * 100) / 100, height: Math.round(nh * 100) / 100 } : f));
    }
  }, []);

  const onPointerUp = useCallback(() => { dragRef.current.active = false; }, []);

  useEffect(() => {
    const opts = { passive: false };
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchmove', onPointerMove, opts);
    window.addEventListener('touchend', onPointerUp);
    window.addEventListener('touchcancel', onPointerUp);
    return () => {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
      window.removeEventListener('touchcancel', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // Save fields
  const doSaveFields = async () => {
    const payload = fields.map(f => ({
      field_type: f.field_type, label: f.label,
      x: Math.round(f.x * 100) / 100, y: Math.round(f.y * 100) / 100,
      width: f.width, height: f.height, font_size: f.font_size,
      font_color: f.font_color, font_family: f.font_family || 'sans-serif',
      font_weight: f.font_weight, text_align: f.text_align,
      default_value: f.default_value || '',
    }));
    const res = await api.put(`/admin/cert-templates/${template.id}/fields/bulk/`, { fields: payload });
    if (selectedField) {
      const idx = fields.findIndex(f => f.id === selectedField);
      if (idx >= 0 && res.data[idx]) setSelectedField(res.data[idx].id);
    }
    setFields(res.data);
    setSavedSnapshot(makeSnapshot(res.data));
    return true;
  };

  const saveFields = async () => {
    setSaving(true);
    try { await doSaveFields(); toast.success('Layout saved!'); }
    catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleIssueCert = async (e) => {
    e.preventDefault();
    if (fields.length === 0) { toast.error('Add at least one field first'); return; }
    try {
      await doSaveFields();
      await api.post('/admin/issued-certificates/', {
        template: template.id, member: issueData.member,
        certificate_number: issueData.certificate_number, title: issueData.title,
        issue_date: issueData.issue_date, expiry_date: issueData.expiry_date || null,
        status: 'issued',
      });
      toast.success('Certificate issued!');
      setShowIssueForm(false);
      setIssueData({ member: '', certificate_number: '', title: '', issue_date: '', expiry_date: '' });
      setMemberSearch('');
      if (onCertIssued) onCertIssued();
    } catch (err) {
      toast.error(err.response?.data?.certificate_number?.[0] || 'Failed to issue');
    }
  };

  const handleBulkIssue = async (e) => {
    e.preventDefault();
    if (selectedMembers.length === 0) { toast.error('Select at least one member'); return; }
    if (fields.length === 0) { toast.error('Add at least one field first'); return; }
    setBulkLoading(true);
    try {
      await doSaveFields();
      const res = await api.post('/admin/issued-certificates/bulk/', {
        template: template.id, title: bulkIssueData.title,
        issue_date: bulkIssueData.issue_date, expiry_date: bulkIssueData.expiry_date || null,
        member_ids: selectedMembers, source: sourceType, source_id: selectedTraining,
      });
      toast.success(`${res.data.created} issued, ${res.data.skipped} skipped`);
      setShowIssueForm(false);
      setBulkIssueData({ title: '', issue_date: '', expiry_date: '' });
      setSelectedMembers([]); setSelectedTraining('');
      if (onCertIssued) onCertIssued();
    } catch (err) { toast.error(err.response?.data?.error || 'Bulk issue failed'); }
    setBulkLoading(false);
  };

  const handleBack = async () => {
    if (isDirty && window.confirm('Save unsaved changes before leaving?')) await saveFields();
    onBack();
  };

  const selField = fields.find(f => f.id === selectedField);
  const sigUrl = signaturePreview || template.signature_image_url;

  return (
    <div className="space-y-4">
      {/* ── Top Bar ── */}
      <div className="bg-white rounded-2xl shadow-sm border px-5 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition" title="Back">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div>
            <h2 className="text-base font-bold text-gray-800 leading-tight">{template.name}</h2>
            <p className="text-xs text-gray-400">{template.width} x {template.height}px &middot; {fields.length} field{fields.length !== 1 ? 's' : ''}</p>
          </div>
          {isDirty && (
            <span className="text-[10px] font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full uppercase tracking-wide animate-pulse">Unsaved</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit(PERMISSIONS.EDIT_CERTIFICATES) && (
            <button onClick={() => setShowIssueForm(!showIssueForm)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition shadow-sm ${showIssueForm ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-[#C8A951] text-white hover:bg-[#b89740]'}`}>
              {showIssueForm ? 'Close Issue Form' : 'Issue Certificate'}
            </button>
          )}
          {canEdit(PERMISSIONS.EDIT_CERTIFICATES) && (
            <button onClick={saveFields} disabled={saving || !isDirty}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition shadow-sm disabled:opacity-50 ${isDirty ? 'bg-[#0B5D1E] text-white hover:bg-[#094d18]' : 'bg-gray-100 text-gray-400 cursor-default'}`}>
              {saving ? 'Saving...' : isDirty ? 'Save Layout' : 'Saved'}
            </button>
          )}
        </div>
      </div>

      {/* ── Issue Form ── */}
      {showIssueForm && (
        <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800 text-sm">Issue Certificate</h3>
            <div className="flex bg-gray-100 rounded-xl p-0.5 gap-0.5">
              <button type="button" onClick={() => setBulkMode(false)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${!bulkMode ? 'bg-white shadow-sm text-[#0B5D1E]' : 'text-gray-500 hover:text-gray-700'}`}>Single</button>
              <button type="button" onClick={() => setBulkMode(true)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${bulkMode ? 'bg-white shadow-sm text-[#0B5D1E]' : 'text-gray-500 hover:text-gray-700'}`}>Bulk Issue</button>
            </div>
          </div>

          {!bulkMode ? (
            <form onSubmit={handleIssueCert} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Member</label>
                  <input type="text" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Search by name or ID..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none text-sm" />
                  {members.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-auto">
                      {members.map(m => (
                        <button key={m.id} type="button"
                          onClick={() => { setIssueData(prev => ({ ...prev, member: m.id })); setMemberSearch(`${m.full_name} (${m.membership_number || ''})`); setMembers([]); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm border-b last:border-b-0">
                          <span className="font-medium text-gray-800">{m.full_name}</span>
                          <span className="text-gray-400 ml-2 text-xs">{m.membership_number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <IssueInput label="Certificate Number" value={issueData.certificate_number} required onChange={v => setIssueData(p => ({ ...p, certificate_number: v }))} />
                <IssueInput label="Certificate Title" value={issueData.title} required onChange={v => setIssueData(p => ({ ...p, title: v }))} />
                <IssueInput label="Issue Date" type="date" value={issueData.issue_date} required onChange={v => setIssueData(p => ({ ...p, issue_date: v }))} />
                <IssueInput label="Expiry Date" type="date" value={issueData.expiry_date} onChange={v => setIssueData(p => ({ ...p, expiry_date: v }))} placeholder="Optional" />
                {canEdit(PERMISSIONS.EDIT_CERTIFICATES) && (
                  <div className="flex items-end">
                    <button type="submit" className="w-full bg-[#0B5D1E] text-white py-2.5 rounded-xl font-semibold hover:bg-[#094d18] transition shadow-sm text-sm">Issue Certificate</button>
                  </div>
                )}
              </div>
            </form>
          ) : (
            <form onSubmit={handleBulkIssue} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Source</label>
                  <select value={sourceType} onChange={e => setSourceType(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none text-sm">
                    <option value="mcpd">MCPD Training</option>
                    <option value="event">Event</option>
                    <option value="agm">AGM</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                    {sourceType === 'mcpd' ? 'Training' : sourceType === 'event' ? 'Event' : 'AGM'}
                  </label>
                  <select value={selectedTraining} onChange={e => setSelectedTraining(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none text-sm">
                    <option value="">-- Select --</option>
                    {trainings.map(tr => <option key={tr.id} value={tr.id}>{tr.title} ({tr.status})</option>)}
                  </select>
                </div>
                <IssueInput label="Certificate Title" value={bulkIssueData.title} required
                  onChange={v => setBulkIssueData(p => ({ ...p, title: v }))} placeholder="e.g. MCPD Certificate" />
                <IssueInput label="Issue Date" type="date" value={bulkIssueData.issue_date} required
                  onChange={v => setBulkIssueData(p => ({ ...p, issue_date: v }))} />
                <IssueInput label="Expiry Date" type="date" value={bulkIssueData.expiry_date}
                  onChange={v => setBulkIssueData(p => ({ ...p, expiry_date: v }))} placeholder="Optional" />
              </div>
              {attendees.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between border-b">
                    <span className="text-sm font-bold text-gray-600">Attendees ({selectedMembers.length}/{attendees.length})</span>
                    <button type="button" onClick={() => setSelectedMembers(selectedMembers.length === attendees.length ? [] : attendees.map(a => a.id))}
                      className="text-xs font-bold text-[#0B5D1E] hover:underline">
                      {selectedMembers.length === attendees.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="max-h-52 overflow-auto divide-y">
                    {attendees.map(a => (
                      <label key={a.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={selectedMembers.includes(a.id)}
                          onChange={() => setSelectedMembers(prev => prev.includes(a.id) ? prev.filter(m => m !== a.id) : [...prev, a.id])}
                          className="rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                        <span className="text-sm font-medium text-gray-700">{a.full_name}</span>
                        <span className="text-xs text-gray-400">{a.membership_number}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {canEdit(PERMISSIONS.EDIT_CERTIFICATES) && (
                <button type="submit" disabled={bulkLoading || selectedMembers.length === 0}
                  className="bg-[#0B5D1E] text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-[#094d18] transition disabled:opacity-50 shadow-sm text-sm">
                  {bulkLoading ? 'Issuing...' : `Issue to ${selectedMembers.length} Member${selectedMembers.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </form>
          )}
        </div>
      )}

      {/* ── Main Designer Layout ── */}
      <div className="flex gap-4 items-start" style={{ minHeight: '500px' }}>
        {/* LEFT: Sidebar */}
        <div className="w-72 flex-shrink-0 space-y-3">
          {/* Tab toggle */}
          <div className="bg-white rounded-2xl shadow-sm border p-1.5 flex gap-1">
            <button onClick={() => setSidebarTab('fields')}
              className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition ${sidebarTab === 'fields' ? 'bg-[#0B5D1E] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}>Add Fields</button>
            <button onClick={() => setSidebarTab('properties')}
              className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition ${sidebarTab === 'properties' ? 'bg-[#0B5D1E] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}>Properties</button>
          </div>

          {/* Fields toolbox */}
          {sidebarTab === 'fields' && (
            <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-4">
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Field Types</h4>
                <div className="grid grid-cols-2 gap-1.5">
                  {canEdit(PERMISSIONS.EDIT_CERTIFICATES) && FIELD_TYPES.map(ft => (
                    <button key={ft.value} onClick={() => addField(ft.value)}
                      className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-medium border border-gray-200 hover:border-[#0B5D1E]/40 hover:bg-[#0B5D1E]/5 text-gray-700 hover:text-[#0B5D1E] transition group">
                      <span className="text-sm opacity-70 group-hover:opacity-100">{ft.icon}</span>
                      <span className="truncate">{ft.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {fields.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Placed Fields ({fields.length})</h4>
                  <div className="space-y-1 max-h-48 overflow-auto">
                    {fields.map(f => (
                      <div key={f.id} onClick={() => { setSelectedField(f.id); setSidebarTab('properties'); }}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-between cursor-pointer transition ${
                          selectedField === f.id ? 'bg-[#0B5D1E]/10 text-[#0B5D1E] ring-1 ring-[#0B5D1E]/20' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <span className="flex items-center gap-2 truncate">
                          <span className="opacity-60">{FIELD_TYPES.find(ft => ft.value === f.field_type)?.icon || ''}</span>
                          {f.label || f.field_type}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                          className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition ml-1 flex-shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Properties panel */}
          {sidebarTab === 'properties' && (
            <div className="bg-white rounded-2xl shadow-sm border p-4">
              {!selField ? (
                <div className="text-center py-8 text-gray-400">
                  <svg className="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  <p className="text-sm">Select a field on the canvas<br/>to edit its properties</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      {FIELD_TYPES.find(ft => ft.value === selField.field_type)?.icon} {selField.field_type}
                    </h4>
                    {canDelete(PERMISSIONS.DELETE_CERTIFICATES) && (
                      <button onClick={() => removeField(selField.id)} title="Remove"
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <PropInput label="Label" value={selField.label} onChange={v => updateField(selField.id, { label: v })} />
                  <div className="grid grid-cols-2 gap-2">
                    <PropInput label="Font Size" type="number" min={8} max={72} value={selField.font_size}
                      onChange={v => updateField(selField.id, { font_size: parseInt(v) || 16 })} />
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 mb-1 uppercase">Color</label>
                      <input type="color" value={selField.font_color}
                        onChange={e => updateField(selField.id, { font_color: e.target.value })}
                        className="w-full h-[34px] border rounded-xl cursor-pointer" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <PropInput label="X %" type="number" min={0} max={95} value={Math.round(selField.x * 10) / 10}
                      onChange={v => updateField(selField.id, { x: parseFloat(v) || 0 })} />
                    <PropInput label="Y %" type="number" min={0} max={95} value={Math.round(selField.y * 10) / 10}
                      onChange={v => updateField(selField.id, { y: parseFloat(v) || 0 })} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <PropInput label="Width %" type="number" min={5} max={95} value={selField.width}
                      onChange={v => updateField(selField.id, { width: parseFloat(v) || 20 })} />
                    <PropInput label="Height %" type="number" min={2} max={50} value={selField.height}
                      onChange={v => updateField(selField.id, { height: parseFloat(v) || 5 })} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 mb-1 uppercase">Font</label>
                    <select value={selField.font_family || 'sans-serif'}
                      onChange={e => updateField(selField.id, { font_family: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                      {FONT_OPTIONS.map(fo => <option key={fo.value} value={fo.value} style={{ fontFamily: fo.value }}>{fo.label}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 mb-1 uppercase">Weight</label>
                      <select value={selField.font_weight} onChange={e => updateField(selField.id, { font_weight: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 mb-1 uppercase">Align</label>
                      <div className="flex border border-gray-200 rounded-xl overflow-hidden">
                        {['left', 'center', 'right'].map(a => (
                          <button key={a} type="button" onClick={() => updateField(selField.id, { text_align: a })}
                            className={`flex-1 py-2 text-xs font-semibold transition ${selField.text_align === a ? 'bg-[#0B5D1E] text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
                            {a === 'left' ? '\u25C0' : a === 'center' ? '\u25C6' : '\u25B6'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(selField.field_type === 'custom' || selField.field_type === 'title') && (
                    <PropInput label={selField.field_type === 'title' ? 'Title Text' : 'Default Value'}
                      value={selField.default_value || ''}
                      onChange={v => updateField(selField.id, { default_value: v })}
                      placeholder={selField.field_type === 'title' ? 'Certificate of Registration' : 'Enter text...'} />
                  )}
                  {selField.field_type === 'signature' && (
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 mb-1 uppercase">Signature Image</label>
                      {sigUrl && <img src={sigUrl} alt="Signature" className="max-h-14 rounded-lg border mb-2" />}
                      <input type="file" accept=".png,.jpg,.jpeg" onChange={handleSignatureUpload}
                        className="w-full text-xs file:mr-2 file:px-3 file:py-1 file:rounded-lg file:border-0 file:bg-[#0B5D1E] file:text-white file:text-xs file:cursor-pointer cursor-pointer" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Canvas */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-2xl shadow-sm border p-3">
            <div ref={canvasRef}
              className="relative w-full overflow-visible rounded-xl border-2 border-dashed border-gray-200 bg-gray-50"
              style={{ aspectRatio: `${template.width}/${template.height}` }}
              onClick={() => setSelectedField(null)}>
              <img src={imgUrl} alt={template.name}
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none rounded-xl" draggable={false} />

              {fields.map(f => {
                const isSelected = selectedField === f.id;
                const isSig = f.field_type === 'signature';
                return (
                  <div key={f.id}
                    onMouseDown={e => startDrag(e, f.id, 'move')}
                    onTouchStart={e => startDrag(e, f.id, 'move')}
                    onClick={e => { e.stopPropagation(); setSelectedField(f.id); setSidebarTab('properties'); }}
                    className={`absolute flex items-center justify-center group/field ${isSelected ? 'ring-2 ring-[#0B5D1E] shadow-lg z-20' : 'border border-dashed border-blue-400/50 hover:border-blue-500 z-10'}`}
                    style={{
                      left: `${f.x}%`, top: `${f.y}%`, width: `${f.width}%`, height: `${f.height}%`,
                      cursor: 'move',
                      backgroundColor: isSelected ? 'rgba(11,93,30,0.06)' : 'rgba(59,130,246,0.04)',
                      borderRadius: '6px', userSelect: 'none', touchAction: 'none',
                      transition: dragRef.current.active && dragRef.current.fieldId === f.id ? 'none' : 'box-shadow 0.15s',
                    }}>
                    {/* Label badge */}
                    <div className={`absolute -top-5 left-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide whitespace-nowrap pointer-events-none ${
                      isSelected ? 'bg-[#0B5D1E] text-white' : 'bg-blue-500/80 text-white opacity-0 group-hover/field:opacity-100'}`}
                      style={{ transition: 'opacity 0.15s' }}>
                      {f.label || f.field_type}
                    </div>
                    {/* Delete btn */}
                    <button onClick={e => { e.stopPropagation(); removeField(f.id); }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] leading-none shadow opacity-0 group-hover/field:opacity-100 hover:bg-red-600 transition-opacity z-30"
                      title="Remove">&times;</button>
                    {/* Resize handle */}
                    {isSelected && (
                      <div onMouseDown={e => startDrag(e, f.id, 'resize')} onTouchStart={e => startDrag(e, f.id, 'resize')}
                        className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-[#0B5D1E] border-2 border-white rounded-full cursor-nwse-resize z-30 shadow-md hover:scale-125 transition-transform"
                        style={{ touchAction: 'none' }} />
                    )}
                    {/* Corner dots when selected */}
                    {isSelected && (
                      <>
                        <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-white border-2 border-[#0B5D1E] rounded-full pointer-events-none z-20" />
                        <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-white border-2 border-[#0B5D1E] rounded-full pointer-events-none z-20" />
                        <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-white border-2 border-[#0B5D1E] rounded-full pointer-events-none z-20" />
                      </>
                    )}
                    {/* Content */}
                    {isSig && sigUrl ? (
                      <img src={sigUrl} alt="Signature" className="max-h-full max-w-full object-contain select-none pointer-events-none" draggable={false} />
                    ) : (
                      <span className="px-1 select-none truncate" style={{
                        fontSize: `clamp(8px, ${f.font_size * 0.7}px, 18px)`,
                        fontFamily: `'${f.font_family || 'sans-serif'}', sans-serif`,
                        color: f.font_color, fontWeight: f.font_weight, textAlign: f.text_align,
                        opacity: 0.7, width: '100%', display: 'block',
                      }}>
                        {(f.field_type === 'custom' || f.field_type === 'title')
                          ? (f.default_value || f.label || 'Text')
                          : `[ ${f.label || f.field_type} ]`}
                      </span>
                    )}
                  </div>
                );
              })}

              {fields.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                  <div className="text-center bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-sm border border-dashed border-gray-300">
                    <p className="text-sm font-semibold text-gray-600">Add fields from the sidebar</p>
                    <p className="text-xs text-gray-400 mt-1">Drag them to position on the certificate</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-2 px-1">
              <p className="text-[10px] text-gray-400">Drag to move &middot; Corner handle to resize &middot; Click to edit properties</p>
              {fields.length > 0 && <p className="text-[10px] text-gray-400">{fields.length} field{fields.length !== 1 ? 's' : ''} placed</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ================================================================
   SECTION 3 — DOWNLOAD CERTIFICATE (shared helper)
   ================================================================ */
async function downloadCertificate(cert, apiInstance, format = 'png') {
  try {
    if (!cert.template) { toast.error('No template linked'); return; }
    const { data: tpl } = await apiInstance.get(`/admin/cert-templates/${cert.template}/`);
    const tplImgUrl = resolveImgUrl(tpl);
    const sigUrl = tpl.signature_image_url || null;
    const fields = tpl.fields_data || [];

    const bg = await new Promise((resolve, reject) => {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img); img.onerror = reject;
      img.src = tplImgUrl;
    });

    let sigImg = null;
    if (sigUrl) {
      sigImg = await new Promise(resolve => {
        const img = new Image(); img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img); img.onerror = () => resolve(null);
        img.src = sigUrl;
      });
    }

    await document.fonts.ready;

    const canvas = document.createElement('canvas');
    canvas.width = bg.naturalWidth; canvas.height = bg.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bg, 0, 0);

    const resolveValue = (f) => {
      const fv = cert.field_values || {};
      switch (f.field_type) {
        case 'full_name': return cert.member_name || fv.full_name || '';
        case 'membership_number': return cert.membership_number || fv.membership_number || '';
        case 'certificate_number': return cert.certificate_number || '';
        case 'issue_date': return cert.issue_date || '';
        case 'expiry_date': return cert.expiry_date || '';
        case 'title': return f.default_value || cert.title || '';
        case 'custom': return f.default_value || f.label || '';
        default: return fv[f.field_type] || fv[f.id] || f.label || '';
      }
    };

    for (const f of fields) {
      const fx = (f.x / 100) * canvas.width;
      const fy = (f.y / 100) * canvas.height;
      const fw = (f.width / 100) * canvas.width;
      const fh = (f.height / 100) * canvas.height;
      if (f.field_type === 'signature' && sigImg) {
        const ratio = Math.min(fw / sigImg.naturalWidth, fh / sigImg.naturalHeight);
        const sw = sigImg.naturalWidth * ratio;
        const sh = sigImg.naturalHeight * ratio;
        ctx.drawImage(sigImg, fx + (fw - sw) / 2, fy + (fh - sh) / 2, sw, sh);
      } else if (f.field_type !== 'signature') {
        const scale = canvas.width / 800;
        const fontSize = Math.round(f.font_size * scale);
        const fontFam = f.font_family && f.font_family !== 'sans-serif' ? `'${f.font_family}', sans-serif` : 'sans-serif';
        ctx.font = `${f.font_weight === 'bold' ? 'bold ' : ''}${fontSize}px ${fontFam}`;
        ctx.fillStyle = f.font_color || '#000';
        ctx.textAlign = f.text_align || 'center';
        const tx = f.text_align === 'left' ? fx : f.text_align === 'right' ? fx + fw : fx + fw / 2;
        ctx.fillText(resolveValue(f), tx, fy + fh / 2 + fontSize / 3, fw);
      }
    }

    const certName = cert.certificate_number || 'certificate';
    if (format === 'pdf') {
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ orientation, unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${certName}.pdf`);
    } else if (format === 'jpeg') {
      const link = document.createElement('a');
      link.download = `${certName}.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.click();
    } else {
      const link = document.createElement('a');
      link.download = `${certName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
    toast.success('Certificate downloaded!');
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate certificate');
  }
}


/* ================================================================
   SECTION 4 — ISSUED CERTIFICATES TAB
   ================================================================ */
function IssuedCertsTab() {
  const { canDelete } = usePermissions();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [downloadMenuId, setDownloadMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const fetchCerts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.append('status', filterStatus);
    if (search) params.append('search', search);
    api.get(`/admin/issued-certificates/?${params}`)
      .then(res => setCerts(res.data.results || res.data))
      .catch(() => toast.error('Failed to load certificates'))
      .finally(() => setLoading(false));
  }, [filterStatus, search]);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this certificate?')) return;
    try {
      await api.patch(`/admin/issued-certificates/${id}/`, { status: 'revoked' });
      setCerts(prev => prev.map(c => c.id === id ? { ...c, status: 'revoked' } : c));
      toast.success('Certificate revoked');
    } catch { toast.error('Failed to revoke'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certificate record permanently?')) return;
    try {
      await api.delete(`/admin/issued-certificates/${id}/`);
      setCerts(prev => prev.filter(c => c.id !== id));
      toast.success('Certificate deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const statusBadge = (s) => {
    const m = { issued: 'bg-emerald-50 text-emerald-700 border-emerald-200', draft: 'bg-amber-50 text-amber-700 border-amber-200', revoked: 'bg-red-50 text-red-700 border-red-200' };
    return m[s] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or certificate #..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none min-w-[140px]">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 border-b">
                {['Cert #', 'Member', 'ID #', 'Title', 'Template', 'Issued', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="8" className="text-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B5D1E] mx-auto" /></td></tr>
              ) : certs.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-16 text-gray-400"><p className="text-sm font-medium">No certificates found</p></td></tr>
              ) : certs.map(c => (
                <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5 text-sm font-mono font-medium text-gray-800">{c.certificate_number}</td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-gray-800">{c.member_name}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{c.membership_number || '\u2014'}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{c.title}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{c.template_name || '\u2014'}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{c.issue_date}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wide border ${statusBadge(c.status)}`}>
                      {c.status_display || c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-1.5 items-center">
                      {c.status !== 'revoked' && (
                        <button title="Download"
                          onClick={e => {
                            if (downloadMenuId === c.id) { setDownloadMenuId(null); return; }
                            const r = e.currentTarget.getBoundingClientRect();
                            setMenuPos({ top: r.bottom + 4, left: r.right });
                            setDownloadMenuId(c.id);
                          }}
                          className="p-2 rounded-lg text-[#0B5D1E] hover:bg-[#0B5D1E]/10 transition">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      )}
                      {c.status === 'issued' && canDelete(PERMISSIONS.DELETE_CERTIFICATES) && (
                        <button onClick={() => handleRevoke(c.id)} className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition" title="Revoke">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        </button>
                      )}
                      {canDelete(PERMISSIONS.DELETE_CERTIFICATES) && (
                        <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Download dropdown portal */}
      {downloadMenuId && (() => {
        const c = certs.find(x => x.id === downloadMenuId);
        if (!c) return null;
        return (
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setDownloadMenuId(null)} />
            <div className="fixed z-[9999] bg-white border rounded-xl shadow-xl min-w-[140px] py-1.5 overflow-hidden"
              style={{ top: menuPos.top, left: menuPos.left, transform: 'translateX(-100%)' }}>
              {[{ fmt: 'png', label: 'PNG Image' }, { fmt: 'jpeg', label: 'JPEG Image' }, { fmt: 'pdf', label: 'PDF Document' }].map(({ fmt, label }) => (
                <button key={fmt} onClick={() => { downloadCertificate(c, api, fmt); setDownloadMenuId(null); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-gray-700 transition">{label}</button>
              ))}
            </div>
          </>
        );
      })()}
    </div>
  );
}


/* ================================================================
   HELPERS
   ================================================================ */
function IssueInput({ label, value, onChange, type = 'text', required, placeholder, ...rest }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none text-sm" {...rest} />
    </div>
  );
}

function PropInput({ label, value, onChange, type = 'text', placeholder, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-gray-400 mb-1 uppercase">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0B5D1E] outline-none" {...rest} />
    </div>
  );
}


/* ================================================================
   DESIGNER TEMPLATE PICKER
   ================================================================ */
function DesignerPicker({ onSelect }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/cert-templates/')
      .then(res => setTemplates(res.data.results || res.data))
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E]" /></div>;

  if (templates.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-16 text-center">
        <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-1">No Templates Available</h3>
        <p className="text-gray-400 text-sm">Upload a certificate template first in the Templates tab</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-gray-800">Select a Template</h2>
        <p className="text-sm text-gray-500 mt-0.5">Choose a template to design fields and issue certificates</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map(t => (
          <div key={t.id} onClick={() => onSelect(t)}
            className="bg-white rounded-2xl shadow-sm border overflow-hidden cursor-pointer hover:shadow-lg hover:border-[#0B5D1E]/30 transition-all duration-300 group">
            <div className="aspect-[1.414/1] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden relative">
              <img src={resolveImgUrl(t)} alt={t.name} className="w-full h-full object-contain p-2" />
              <div className="absolute inset-0 bg-[#0B5D1E]/0 group-hover:bg-[#0B5D1E]/5 transition-all duration-300 flex items-center justify-center">
                <span className="bg-[#0B5D1E] text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  Open Designer
                </span>
              </div>
            </div>
            <div className="p-4 border-t">
              <h3 className="font-bold text-gray-800 truncate text-sm">{t.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-400">{t.width} x {t.height}px</span>
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">{t.fields_data?.length || 0} fields</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ================================================================
   MAIN COMPONENT — Tab Router
   ================================================================ */
export default function AdminCertificates() {
  const [activeTab, setActiveTab] = useState('templates');
  const [editTemplate, setEditTemplate] = useState(null);

  const handleTabChange = (tabId) => {
    if (activeTab === 'designer' && tabId !== 'designer') setEditTemplate(null);
    setActiveTab(tabId);
  };

  const handleDesign = (template) => {
    setEditTemplate(template);
    setActiveTab('designer');
  };

  const tabs = [
    { id: 'templates', label: 'Templates', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )},
    { id: 'designer', label: 'Designer', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    )},
    { id: 'issued', label: 'Issued Certificates', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    )},
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Certificate Manager</h1>
        <p className="text-sm text-gray-500 mt-0.5">Create, design, and issue certificates to members</p>
      </div>
      <div className="flex border-b border-gray-200 mb-6 gap-1">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all ${
              activeTab === tab.id ? 'border-[#0B5D1E] text-[#0B5D1E]' : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'}`}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'templates' && <TemplatesTab onDesign={handleDesign} />}
      {activeTab === 'issued' && <IssuedCertsTab />}
      {activeTab === 'designer' && !editTemplate && <DesignerPicker onSelect={handleDesign} />}
      {activeTab === 'designer' && editTemplate && (
        <DesignerTab template={editTemplate} onBack={() => setEditTemplate(null)} onCertIssued={() => setActiveTab('issued')} />
      )}
    </div>
  );
}
